import { createTheme, responsiveFontSizes } from "@mui/material/styles";

let theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    mode: "light",
    primary: {
      main: "#2B8A91", // Teal color
      light: "#45A1A8",
      dark: "#1E6065",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FF6B35", // Bright orange
      light: "#FF8459",
      dark: "#E85A27",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#F44336",
    },
    success: {
      main: "#4CAF50",
    },
    background: {
      default: "#FAFAFA",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#333333",
      secondary: "#666666",
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
      letterSpacing: "-0.01562em",
      "@media (min-width:600px)": {
        fontSize: "3rem",
      },
      "@media (min-width:900px)": {
        fontSize: "3.5rem",
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: "2.2rem",
      lineHeight: 1.2,
      letterSpacing: "-0.00833em",
      "@media (min-width:600px)": {
        fontSize: "2.5rem",
      },
      "@media (min-width:900px)": {
        fontSize: "3rem",
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.8rem",
      lineHeight: 1.3,
      letterSpacing: "0em",
      "@media (min-width:600px)": {
        fontSize: "2.2rem",
      },
      "@media (min-width:900px)": {
        fontSize: "2.5rem",
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.35,
      letterSpacing: "0.00735em",
      "@media (min-width:600px)": {
        fontSize: "1.8rem",
      },
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.2rem",
      lineHeight: 1.4,
      letterSpacing: "0em",
      "@media (min-width:600px)": {
        fontSize: "1.5rem",
      },
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.1rem",
      lineHeight: 1.6,
      letterSpacing: "0.0075em",
      "@media (min-width:600px)": {
        fontSize: "1.25rem",
      },
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
    },
    body1: {
      fontSize: "1rem",
      "@media (max-width:600px)": {
        fontSize: "0.9375rem",
      },
    },
    body2: {
      fontSize: "0.875rem",
      "@media (max-width:600px)": {
        fontSize: "0.8125rem",
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: (factor) => `${0.5 * factor}rem`,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.08)",
          transition: "all 0.2s ease-in-out",
          whiteSpace: "nowrap",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.12)",
          },
          "@media (max-width:600px)": {
            padding: "8px 16px",
            fontSize: "0.875rem",
          },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #2B8A91 0%, #45A1A8 100%)",
        },
        containedSecondary: {
          background: "linear-gradient(135deg, #FF6B35 0%, #FF8459 100%)",
        },
        outlined: {
          borderWidth: "2px",
          "&:hover": {
            borderWidth: "2px",
          },
        },
        sizeSmall: {
          "@media (max-width:600px)": {
            padding: "4px 10px",
            fontSize: "0.8125rem",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.06)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          overflow: "hidden",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
          "@media (max-width:600px)": {
            fontSize: "0.75rem",
            height: "28px",
          },
        },
        icon: {
          "@media (max-width:600px)": {
            fontSize: "1rem",
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          "@media (max-width:600px)": {
            padding: "0 16px",
          },
        },
      },
    },
    MuiStack: {
      defaultProps: {
        useFlexGap: true,
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          "@media (max-width:600px)": {
            minWidth: "32px",
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          "@media (max-width:600px)": {
            minHeight: "56px",
            paddingLeft: "16px",
            paddingRight: "16px",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "@media (max-width:600px)": {
            padding: "8px",
          },
        },
      },
    },
  },
});

// Apply responsive font sizes
theme = responsiveFontSizes(theme);

export default theme;
